import Decimal from 'decimal.js';
import { either, equals, isNil, pipe, props } from 'ramda';
import { Union } from 'ts-toolbelt';

export type TripletProps = {
  values: Union.Nullable<any>;
  pathMin: string;
  pathMax: string;
  pathAvg: string;
};

type TripletValue = string | number;
type Triplet = [TripletValue, TripletValue, TripletValue];

// Cubejs should return always a valid number as string,
// however we avoid displaying an error to the user on
// invalid numbers by simply checking for NaN exactly
const isInvalidDecimal = either(isNil, pipe(Number, equals(NaN)));

const toPreciseNumber = (value: TripletValue, precision: number): number =>
  new Decimal(value).toDecimalPlaces(precision).toNumber();

export const toValueTriplet = ({ values, pathMin, pathAvg, pathMax }: TripletProps) => {
  if (isNil(values)) {
    return null;
  }

  const triplet = props([pathMin, pathMax, pathAvg], values);
  if (triplet.some(isInvalidDecimal)) {
    return null;
  }

  // enforcing type safety as linting doesn't catch the `.some()` check
  const [min, max, avg] = triplet as Triplet;
  return {
    min: toPreciseNumber(min, 2),
    max: toPreciseNumber(max, 2),
    avg: toPreciseNumber(avg, 2),
  };
};
